import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listLots, quarantineLot } from "@/lib/lots.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/inventory/expiring")({ component: ExpiringPage });

const BUCKETS = [7, 30, 60, 90] as const;

function ExpiringPage() {
  const { tenantId, customerId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listLots);
  const qFn = useServerFn(quarantineLot);
  const [days, setDays] = useState<number>(30);

  const { data, isLoading } = useQuery({
    queryKey: ["lots-expiring", tenantId, customerId, days],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, customerId: customerId ?? null, status: "active", expiringWithinDays: days } }),
    placeholderData: keepPreviousData,
  });
  const skuMap = Object.fromEntries((data?.skus ?? []).map((s: any) => [s.id, s]));
  const rows = (data?.rows ?? []) as any[];

  const quarantine = useMutation({
    mutationFn: (id: string) => qFn({ data: { tenantId: tenantId!, lotId: id, reason: "qa_inspection" } }),
    onSuccess: () => { toast.success("Quarantined"); qc.invalidateQueries({ queryKey: ["lots-expiring"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader title="Expiring Inventory"
        description="Active lots approaching expiry. Quarantine to remove from FEFO allocation."
        meta={<Link to="/app/inventory/lots" className="text-xs text-primary underline">All lots →</Link>} />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <TabsList>
            {BUCKETS.map((d) => <TabsTrigger key={d} value={String(d)}>≤ {d} days</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="border rounded-md bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Lot</TableHead><TableHead>SKU</TableHead>
              <TableHead>Expiry</TableHead><TableHead className="text-right">On hand</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right w-40">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!isLoading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nothing expiring in this window.</TableCell></TableRow>}
              {rows.map((l: any) => {
                const sku = skuMap[l.sku_id];
                const dte = l.expiry_date ? Math.ceil((new Date(l.expiry_date).getTime() - Date.now()) / 86400000) : null;
                return (
                  <TableRow key={l.id}>
                    <TableCell className="font-mono text-xs">{l.lot_code}</TableCell>
                    <TableCell className="text-xs">{sku ? `${sku.sku_code} — ${sku.name}` : "—"}</TableCell>
                    <TableCell className="text-xs">{l.expiry_date} <span className={`ml-1 text-[11px] ${dte != null && dte <= 7 ? "text-destructive" : "text-muted-foreground"}`}>({dte}d)</span></TableCell>
                    <TableCell className="text-right tabular-nums">{l.on_hand_qty}</TableCell>
                    <TableCell><Badge variant="secondary">{l.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" disabled={quarantine.isPending} onClick={() => quarantine.mutate(l.id)}>Quarantine</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}